// The constellation picker: a sheet over the Horoscope (from the bottom on a
// phone, centered elsewhere) listing every constellation the player wrote
// that night, each on its patch of sky with its word, its stars and the sky
// it was found in. The current choice is marked; one already holding another
// sign says so, and may be chosen again. A tap chooses and closes; Clear
// empties the sign; Escape or a tap on the scrim closes.
import { VARIANT_NAMES } from '@wits/core';
import { useEffect, useRef } from 'react';
import type { Sign, SignConstellation } from '../../systems/api/types';
import { Button, IconButton } from '../components/Button';
import { Icon } from '../components/Icon';
import { ConstellationArt } from '../sky/ConstellationArt';
import { SIGN_LABELS, SIGN_MEANINGS, SignGlyph } from './signs';

interface Props {
  sign: Sign;
  constellations: SignConstellation[];
  picks: Partial<Record<Sign, string>>;
  sky: { top: string; mid: string };
  onPick: (id: string | null) => void;
  onClose: () => void;
}

export function SignPicker({ sign, constellations, picks, sky, onPick, onClose }: Props) {
  const sheet = useRef<HTMLDivElement>(null);
  const current = picks[sign] ?? null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const first = sheet.current?.querySelector<HTMLButtonElement>('.picker-card[aria-pressed="true"]') ?? sheet.current?.querySelector<HTMLButtonElement>('.picker-card');
    first?.focus({ preventScroll: true });
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const others = (id: string) => (Object.keys(picks) as Sign[]).filter((s) => s !== sign && picks[s] === id);

  return (
    <div className="picker-root">
      <div className="picker-scrim" onClick={onClose} />
      <div ref={sheet} className="picker panel" role="dialog" aria-modal="true" aria-labelledby="picker-title">
        <header className="picker-head">
          <SignGlyph sign={sign} className="picker-glyph" />
          <div className="picker-titles">
            <h2 id="picker-title" className="picker-title">
              Your {SIGN_LABELS[sign]}
            </h2>
            <p className="picker-meaning quiet">{SIGN_MEANINGS[sign]}</p>
          </div>
          <IconButton icon="close" label="Close" onClick={onClose} />
        </header>
        <ul className="picker-grid">
          {constellations.map((c) => {
            const chosen = c.id === current;
            const elsewhere = others(c.id);
            return (
              <li key={c.id}>
                <button type="button" className="picker-card" aria-pressed={chosen} onClick={() => onPick(c.id)}>
                  <ConstellationArt figure={c} width={296} height={148} padX={30} padY={24} maxScale={0.42} top={sky.top} mid={sky.mid} className="picker-art" />
                  <span className="picker-meta">
                    <span className="picker-word mono">{c.word}</span>
                    {chosen && <Icon name="check" small className="picker-check" />}
                  </span>
                  <span className="picker-detail label-mono quiet">
                    {c.stars} {c.stars === 1 ? 'star' : 'stars'} · {VARIANT_NAMES[c.variant]}
                  </span>
                  {elsewhere.length > 0 && (
                    <span className="picker-also label-mono">
                      {elsewhere.map((s) => (
                        <SignGlyph key={s} sign={s} />
                      ))}
                      Also your {elsewhere.map((s) => SIGN_LABELS[s]).join(' and ')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {constellations.length === 0 && <p className="picker-none quiet">None of tonight's constellations can be a sign.</p>}
        {current && (
          <footer className="picker-foot">
            <Button variant="ghost" small onClick={() => onPick(null)}>
              Clear {SIGN_LABELS[sign]}
            </Button>
          </footer>
        )}
      </div>
    </div>
  );
}
